// CELPIP Writing exam screen: task instructions interstitial, then the real
// test layout: prompt + bullets left, response editor right, live word count,
// task timer with auto-submit. After submit: gates + AI feedback (or queued).
import React, { useCallback, useEffect, useRef, useState } from 'react';
import { Link, useNavigate, useParams, useSearchParams } from 'react-router-dom';
import { ArrowLeft, Clock, PenTool } from 'lucide-react';

import { api } from '@/lib/api';
import { Button } from '@/components/ui/button';
import { cn } from '@/lib/utils';

// `submit()` unconditionally navigates to the review page on success, so the
// PHASE.DONE render + polling effect that used to display feedback inline
// were unreachable. Deleted along with their `result`/`marking` state,
// `CheckCircle2`/`AssignmentNextCTA`/gate-flag imports, and the DONE enum
// value — nothing in this file can reach that state any more.
const PHASE = { INTRO: 'INTRO', RUN: 'RUN' };

const TASK_META = {
  write_email: {
    title: 'Write Email',
    instructions: [
      'Read the situation and write an email about it.',
      'The screen asks for 80-120 words. Under 30 or over 140 words scores ZERO for the whole task.',
      'Address every bullet point and keep the email frame: greeting, purpose, paragraphs, sign-off.',
      'You have 9 minutes, including planning and proofreading.',
    ],
  },
  summarize_written_text: {
    title: 'Summarize Written Text',
    instructions: [
      'Read the passage, then write a summary of 25-50 words.',
      'Outside the 25-50 word range this response scores 0. Watch the word counter.',
      'Capture the main argument and its key qualifier in your own words.',
      'You have 10 minutes.',
    ],
  },
  writing_t1_email: {
    title: 'Writing Task 1: Writing an Email',
    instructions: [
      'Read the situation and write an email of about 150-200 words.',
      'Address every bullet point in the task.',
      'Match your tone to the person you are writing to.',
      'Your time includes planning and proofreading.',
    ],
  },
  writing_t2_survey: {
    title: 'Writing Task 2: Responding to Survey Questions',
    instructions: [
      'Read the survey question and choose ONE option.',
      'Write about 150-200 words defending your choice.',
      'State your position in the first sentence and hold it.',
      'Your time includes planning and proofreading.',
    ],
  },
};

function formatTime(sec) {
  const m = Math.floor(sec / 60);
  const s = sec % 60;
  return `${m}:${String(s).padStart(2, '0')}`;
}

function countWords(text) {
  return (text.match(/\b[\w''-]+\b/g) || []).length;
}

export default function WritingRunner() {
  const { exam, taskType } = useParams();
  const [searchParams] = useSearchParams();
  const setParam = searchParams.get('set');
  const navigate = useNavigate();
  const meta = TASK_META[taskType] || { title: 'Writing task', instructions: [] };

  const [item, setItem] = useState(null);
  const [phase, setPhase] = useState(PHASE.INTRO);
  const [text, setText] = useState('');
  const [secondsLeft, setSecondsLeft] = useState(0);
  const [busy, setBusy] = useState(false);
  const [loadError, setLoadError] = useState('');   // full-screen: initial load never resolved
  const [submitError, setSubmitError] = useState(''); // inline: submit failed but the essay is still on screen
  const textRef = useRef('');
  const submittingRef = useRef(false);
  const startedAtRef = useRef(null);

  const load = useCallback(async () => {
    setLoadError('');
    setSubmitError('');
    setText('');
    textRef.current = '';
    setPhase(PHASE.INTRO);
    submittingRef.current = false;
    try {
      const res = await api.practiceSets(exam, taskType, 1, setParam);
      // One-shot enforcement: if the student already used their attempt for
      // this set, route them straight to the read-only review instead of the
      // runner. Handles direct-URL nav (SetPicker already routes around).
      if (res.data.review_submission_id) {
        navigate(`/${exam}/submissions/${res.data.review_submission_id}/review`, { replace: true });
        return;
      }
      const it = res.data.sets[0];
      setItem(it);
      setSecondsLeft(it.response_spec?.time_limit_sec || 1620);
    } catch (err) {
      setLoadError(err.response?.data?.detail || 'No writing prompt available.');
    }
  }, [exam, taskType, setParam, navigate]);

  useEffect(() => {
    load();
  }, [load]);

  const submit = useCallback(async (currentItem) => {
    if (submittingRef.current || !currentItem) return;
    submittingRef.current = true;
    setSubmitError('');
    setBusy(true);
    try {
      const seconds_used = startedAtRef.current
        ? Math.round((Date.now() - startedAtRef.current) / 1000)
        : 0;
      const mockForm = searchParams.get('mock_form');
      const res = await api.submitWriting(exam, {
        set_id: currentItem.set_id,
        text: textRef.current,
        seconds_used,
        ...(mockForm ? { mock_form: Number(mockForm) } : {}),
      });
      // Backend now returns in ~200ms with status=queued and grades in the
      // background. Redirect to the review page immediately — it polls until
      // the mark lands, so the student sees a proper "Marking… ~30s" state
      // instead of watching a frozen Submit button.
      const s = res.data.submission;
      navigate(`/${exam}/submissions/${s.submission_id}/review`, { replace: true });
    } catch (err) {
      const detail = err.response?.data?.detail;
      if (err.response?.status === 409 && detail?.submission_id) {
        // The student already used their attempt (race / double-submit / two
        // tabs). Route them to the read-only review — no error banner.
        navigate(`/${exam}/submissions/${detail.submission_id}/review`, { replace: true });
        return;
      }
      // Any other error MUST keep the RUN phase and the essay on screen.
      // Previously we set a top-level `error` state that replaced the whole
      // render tree with "Submission failed — Back to practice", orphaning
      // the student's essay in state with no way to retry. Now we stay in
      // RUN, show an inline banner + retry button, and preserve textRef.
      setSubmitError(
        typeof detail === 'string' ? detail : (detail?.message || 'We could not submit your response. Try again — your response is safe here.'),
      );
      submittingRef.current = false;
      setBusy(false);
    }
  }, [exam, navigate, searchParams]);

  useEffect(() => {
    if (phase !== PHASE.RUN) return undefined;
    const interval = setInterval(() => {
      setSecondsLeft((s) => {
        if (s <= 1) {
          clearInterval(interval);
          submit(item);
          return 0;
        }
        return s - 1;
      });
    }, 1000);
    return () => clearInterval(interval);
  }, [phase, item, submit]);

  if (loadError) {
    return (
      <div className="flex min-h-screen flex-col items-center justify-center gap-3 bg-slate-100">
        <p className="text-slate-600">{loadError}</p>
        <Link to={`/${exam}/practice`} className="text-sm font-medium text-blue-800 hover:underline">
          Back to practice
        </Link>
      </div>
    );
  }
  if (!item) return <div className="flex min-h-screen items-center justify-center bg-slate-100 text-slate-500">Preparing…</div>;

  const spec = item.response_spec || {};
  const words = countWords(text);

  const Header = ({ showTimer }) => (
    <header className="bg-blue-900 text-white">
      <div className="mx-auto flex max-w-6xl items-center justify-between px-6 py-3">
        <div className="flex items-center gap-3">
          <PenTool className="h-4 w-4 opacity-80" />
          <div>
            <p className="text-[11px] uppercase tracking-wider text-blue-200">{exam.toUpperCase()} practice • Writing</p>
            <p className="text-sm font-semibold">{meta.title}</p>
          </div>
        </div>
        {showTimer && (
          <span
            className={cn(
              'flex items-center gap-1.5 rounded bg-blue-950/60 px-2.5 py-1 text-sm font-semibold tabular-nums',
              secondsLeft <= 120 && 'bg-red-600'
            )}
            data-testid="writing-timer"
          >
            <Clock className="h-3.5 w-3.5" />
            {formatTime(secondsLeft)}
          </span>
        )}
      </div>
    </header>
  );

  if (phase === PHASE.INTRO) {
    return (
      <div className="min-h-screen bg-slate-100">
        <Header showTimer={false} />
        <main className="mx-auto max-w-3xl px-6 py-12">
          <div className="border border-slate-300 bg-white p-10 shadow-sm">
            <p className="text-xs font-semibold uppercase tracking-wider text-blue-900">Instructions</p>
            <h1 className="mt-2 text-xl font-semibold text-slate-900">{meta.title}</h1>
            <ul className="mt-6 space-y-3">
              {meta.instructions.map((line, i) => (
                <li key={i} className="flex gap-3 text-[15px] leading-6 text-slate-700">
                  <span className="mt-2 h-1.5 w-1.5 shrink-0 rounded-full bg-blue-900" />
                  {line}
                </li>
              ))}
              <li className="flex gap-3 text-[15px] leading-6 text-slate-700">
                <span className="mt-2 h-1.5 w-1.5 shrink-0 rounded-full bg-blue-900" />
                You have {formatTime(spec.time_limit_sec || 1620)} for this task.
              </li>
            </ul>
            <div className="mt-10 flex items-center justify-between">
              <Link to={`/${exam}/practice`} className="inline-flex items-center gap-1 text-sm text-slate-500 hover:text-slate-800" data-testid="writing-exit">
                <ArrowLeft className="h-4 w-4" /> Exit
              </Link>
              <Button
                size="lg"
                className="bg-blue-900 px-10 hover:bg-blue-800"
                data-testid="writing-start"
                onClick={() => {
                  startedAtRef.current = Date.now();
                  setPhase(PHASE.RUN);
                }}
              >
                START
              </Button>
            </div>
          </div>
        </main>
      </div>
    );
  }

  if (phase === PHASE.RUN) {
    return (
      <div className="min-h-screen bg-slate-100">
        <Header showTimer />
        <main className="mx-auto max-w-6xl px-6 py-8">
          <div className="grid gap-6 lg:grid-cols-5">
            <div className="lg:col-span-2">
              <div className="lg:sticky lg:top-6 border border-slate-300 bg-white shadow-sm">
                <div className="border-b border-slate-200 bg-slate-50 px-5 py-2.5">
                  <p className="text-xs font-semibold uppercase tracking-wide text-slate-500">Task</p>
                </div>
                <div className="px-5 py-4">
                  {item.stimulus?.text && (
                    <p className="mb-4 whitespace-pre-wrap border-b border-slate-100 pb-4 text-[15px] leading-7 text-slate-800">
                      {item.stimulus.text}
                    </p>
                  )}
                  <p className="text-[15px] leading-7 text-slate-800">{spec.prompt_text}</p>
                  {spec.bullets && (
                    <ul className="mt-4 space-y-2">
                      {spec.bullets.map((b, i) => (
                        <li key={i} className="flex gap-2.5 text-sm leading-6 text-slate-700">
                          <span className="mt-2 h-1 w-1 shrink-0 rounded-full bg-slate-500" />
                          {b}
                        </li>
                      ))}
                    </ul>
                  )}
                  {spec.register_note && (
                    <p className="mt-4 border-t border-slate-100 pt-3 text-xs leading-5 text-slate-500">{spec.register_note}</p>
                  )}
                </div>
              </div>
            </div>

            <div className="lg:col-span-3">
              <div className="border border-slate-300 bg-white shadow-sm">
                <div className="flex items-center justify-between border-b border-slate-200 bg-slate-50 px-5 py-2.5">
                  <p className="text-xs font-semibold uppercase tracking-wide text-slate-500">Your response</p>
                  <p className="text-xs font-semibold tabular-nums text-slate-600" data-testid="writing-wordcount">
                    Word count: {words}
                    <span className="ml-1 font-normal text-slate-400">({spec.word_guidance})</span>
                  </p>
                </div>
                <textarea
                  value={text}
                  data-testid="writing-editor"
                  onChange={(e) => {
                    setText(e.target.value);
                    textRef.current = e.target.value;
                  }}
                  spellCheck={false}
                  className="block h-[52vh] w-full resize-none px-5 py-4 text-[15px] leading-7 text-slate-900 outline-none"
                  placeholder="Write your response here."
                />
              </div>
              <div className="mt-6 flex flex-col items-end gap-3">
                {submitError && (
                  <div
                    className="w-full border-l-2 border-red-500 bg-red-50/60 px-4 py-3 text-sm text-red-800"
                    data-testid="writing-submit-error"
                  >
                    <p className="font-semibold">Submit didn&apos;t go through.</p>
                    <p className="mt-1 text-xs text-red-700/90">{submitError}</p>
                    <p className="mt-1 text-xs text-slate-600">
                      Your response is still here. Click SUBMIT again to retry — nothing has been lost.
                    </p>
                  </div>
                )}
                <Button
                  size="lg"
                  className="bg-blue-900 px-10 hover:bg-blue-800"
                  disabled={busy || words === 0}
                  data-testid="writing-submit"
                  onClick={() => submit(item)}
                >
                  {busy ? 'Submitting…' : submitError ? 'RETRY SUBMIT' : 'SUBMIT'}
                </Button>
              </div>
            </div>
          </div>
        </main>
        {/* Full-page overlay so a slow multipart upload never feels like the
            page froze. The click is instantly acknowledged even before we
            navigate to the review page. */}
        {busy && (
          <div className="fixed inset-0 z-50 flex items-center justify-center bg-slate-900/40 backdrop-blur-sm" data-testid="writing-submitting-overlay">
            <div className="flex items-center gap-3 border border-slate-200 bg-white px-6 py-4 shadow-xl">
              <span className="h-4 w-4 shrink-0 animate-spin rounded-full border-2 border-slate-300 border-t-blue-900" />
              <p className="text-sm font-medium text-slate-800">Submitting your response…</p>
            </div>
          </div>
        )}
      </div>
    );
  }

  // Every reachable phase (INTRO / RUN) returns above; anything else is a
  // logic error we'd rather blank-screen than fake feedback for.
  return null;
}
